import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, MapPin, Eye, EyeOff, Users, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

const COLORS = ["#0052FF", "#E11D48", "#059669", "#D97706", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#4F46E5", "#0D9488", "#B91C1C"];
const STATUS = { done: { label: "Fatto", fill: "#10B981" }, ko: { label: "KO", fill: "#EF4444" }, pending: { label: "Da fare", fill: "#FFFFFF" } };
const REFRESH_MS = 120000;
const todayIso = () => new Date().toISOString().slice(0, 10);

const techIcon = (name, color) => L.divIcon({
  className: "",
  iconSize: null,
  iconAnchor: [14, 34],
  html: `<div style="display:flex;align-items:center;gap:4px;transform:translateY(-4px)">
    <div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>
    <div style="background:${color};color:#fff;font:700 11px/1.2 sans-serif;padding:3px 6px;border-radius:2px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3)">${name}</div>
  </div>`,
});

function FitBounds({ points, fitKey }) {
  const map = useMap();
  useEffect(() => {
    if (points.length) map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
  }, [fitKey, points.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 14, { duration: 0.6 });
  }, [target, map]);
  return null;
}

export default function TecniciInCampoPage() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState({});
  const [flyTarget, setFlyTarget] = useState(null);
  const [fitKey, setFitKey] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data: d } = await api.get("/field-map", { params: { date } });
      setData(d);
      if (!silent) setFitKey((k) => k + 1);
      clearTimeout(pollRef.current);
      if (d.pending_geocode > 0) pollRef.current = setTimeout(() => load(true), 5000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento mappa");
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => {
    load(false);
    const iv = setInterval(() => load(true), REFRESH_MS);
    return () => { clearInterval(iv); clearTimeout(pollRef.current); };
  }, [load]);

  const techs = useMemo(() => (data?.technicians || []).map((t, i) => ({ ...t, color: COLORS[i % COLORS.length] })), [data]);
  const visible = techs.filter((t) => !hidden[t.id]);
  const allPoints = visible.flatMap((t) => t.stops.filter((s) => s.lat != null).map((s) => [s.lat, s.lng]));
  const totals = techs.reduce((a, t) => ({ done: a.done + t.counts.done, ko: a.ko + t.counts.ko, pending: a.pending + t.counts.pending }), { done: 0, ko: 0, pending: 0 });

  return (
    <div className="space-y-4" data-testid="field-map-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Territorio</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Visualizza tecnici in campo</h1>
          <p className="mt-1 text-sm text-slate-600">Posizione stimata dall'ultimo impianto Fatto/KO o dal primo appuntamento · aggiornamento automatico ogni 2 minuti</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <TechPicker techs={techs} hidden={hidden} setHidden={setHidden} />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 rounded-sm" data-testid="field-map-date" />
          <Button variant="outline" className="rounded-sm" onClick={() => load(false)} disabled={loading} data-testid="field-map-refresh">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Aggiorna
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="field-map-summary">
        <Chip label="Tecnici" value={techs.length} />
        <Chip label="Impianti" value={data?.total ?? 0} />
        <Chip label="Fatti" value={totals.done} cls="text-emerald-700" />
        <Chip label="KO" value={totals.ko} cls="text-red-600" />
        <Chip label="Da fare" value={totals.pending} cls="text-slate-700" />
        {data?.pending_geocode > 0 && (
          <span className="flex items-center gap-1 rounded-sm bg-blue-50 px-2 py-1 font-semibold text-[#0052FF]" data-testid="field-map-geocoding">
            <Loader2 className="h-3 w-3 animate-spin" /> Localizzazione di {data.pending_geocode} indirizzi in corso…
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
        <TechList techs={techs} hidden={hidden} setHidden={setHidden} onFocus={(t) => t.current.lat != null && setFlyTarget([t.current.lat, t.current.lng])} />
        <div className="relative z-0 h-[65vh] min-h-[420px] overflow-hidden rounded-sm border border-slate-200" data-testid="field-map-canvas">
          <MapContainer center={[45.47, 9.0]} zoom={10} className="h-full w-full" scrollWheelZoom>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={allPoints} fitKey={fitKey} />
            <FlyTo target={flyTarget} />
            {visible.map((t) => <TechLayer key={t.id} tech={t} />)}
          </MapContainer>
          {data && techs.length === 0 && (
            <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
              <div className="rounded-sm bg-white/90 px-4 py-3 text-sm text-slate-500 shadow">Nessun impianto assegnato in questa data</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, cls = "text-slate-900" }) {
  return (
    <span className="rounded-sm border border-slate-200 bg-white px-2 py-1">
      <span className="text-slate-500">{label}: </span><b className={`font-mono ${cls}`}>{value}</b>
    </span>
  );
}

function TechLayer({ tech }) {
  const pts = tech.stops.filter((s) => s.lat != null);
  const cur = tech.current;
  return (
    <>
      {pts.length > 1 && <Polyline positions={pts.map((s) => [s.lat, s.lng])} pathOptions={{ color: tech.color, weight: 3, opacity: 0.6, dashArray: "6 6" }} />}
      {pts.map((s, i) => (
        <CircleMarker key={s.id} center={[s.lat, s.lng]} radius={8}
          pathOptions={{ color: tech.color, weight: 3, fillColor: STATUS[s.status]?.fill || "#fff", fillOpacity: 1 }}>
          <Tooltip direction="top" offset={[0, -6]}>{tech.name} · {i + 1}. {s.time || "—"}</Tooltip>
          <Popup>
            <div className="space-y-0.5 text-xs">
              <div className="font-bold" style={{ color: tech.color }}>{tech.name}</div>
              <div><b>{s.time || "—"}</b> · {STATUS[s.status]?.label || s.status}{s.ko_reason ? ` (${s.ko_reason})` : ""}</div>
              <div>{s.customer}</div>
              <div className="text-slate-500">{s.address} {s.city}</div>
              {s.ref && <div className="font-mono text-slate-400">{s.ref}</div>}
              {s.precision === "comune" && <div className="text-amber-600">Posizione approssimata al comune</div>}
            </div>
          </Popup>
        </CircleMarker>
      ))}
      {cur.lat != null && (
        <Marker position={[cur.lat, cur.lng]} icon={techIcon(tech.name, tech.color)} zIndexOffset={1000}>
          <Popup><div className="text-xs"><b>{tech.name}</b><br />Posizione stimata: {cur.source}<br />{cur.label}</div></Popup>
        </Marker>
      )}
    </>
  );
}

function TechPicker({ techs, hidden, setHidden }) {
  const shown = techs.filter((t) => !hidden[t.id]);
  const label = !techs.length ? "Nessun tecnico" : shown.length === techs.length ? "Tutti i tecnici" : shown.length === 1 ? shown[0].name : `${shown.length} tecnici selezionati`;
  const setAll = (hide) => setHidden(Object.fromEntries(techs.map((t) => [t.id, hide])));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-56 justify-between rounded-sm" disabled={!techs.length} data-testid="field-map-tech-picker">
          <span className="flex min-w-0 items-center gap-2"><Users className="h-4 w-4 shrink-0" /><span className="truncate">{label}</span></span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-sm p-2">
        <div className="mb-2 flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 flex-1 text-xs" onClick={() => setAll(false)} data-testid="field-map-pick-all">Tutti</Button>
          <Button size="sm" variant="ghost" className="h-7 flex-1 text-xs" onClick={() => setAll(true)} data-testid="field-map-pick-none">Nessuno</Button>
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {techs.map((t) => (
            <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`field-map-pick-${t.id}`}>
              <Checkbox checked={!hidden[t.id]} onCheckedChange={(v) => setHidden((h) => ({ ...h, [t.id]: !v }))} />
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: t.color }} />
              <span className="truncate">{t.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TechList({ techs, hidden, setHidden, onFocus }) {
  return (
    <div className="max-h-[65vh] space-y-2 overflow-y-auto" data-testid="field-map-tech-list">
      {techs.length === 0 && <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Nessun tecnico in campo</div>}
      {techs.map((t) => (
        <div key={t.id} className={`rounded-sm border bg-white p-3 transition-opacity ${hidden[t.id] ? "opacity-50" : ""}`} style={{ borderLeft: `4px solid ${t.color}` }} data-testid={`field-map-tech-${t.id}`}>
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="flex min-w-0 items-center gap-2 text-left" onClick={() => onFocus(t)} data-testid={`field-map-focus-${t.id}`}>
              <MapPin className="h-4 w-4 shrink-0" style={{ color: t.color }} />
              <span className="truncate font-semibold text-slate-900">{t.name}</span>
            </button>
            <button type="button" onClick={() => setHidden((h) => ({ ...h, [t.id]: !h[t.id] }))} title={hidden[t.id] ? "Mostra" : "Nascondi"} data-testid={`field-map-toggle-${t.id}`}>
              {hidden[t.id] ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-500" />}
            </button>
          </div>
          <div className="mt-1 flex gap-3 font-mono text-xs">
            <span className="text-emerald-700">{t.counts.done} fatti</span>
            <span className="text-red-600">{t.counts.ko} KO</span>
            <span className="text-slate-600">{t.counts.pending} da fare</span>
          </div>
          <div className="mt-1 truncate text-[11px] text-slate-500" title={t.current.label}>
            {t.current.source}: {t.current.label || "—"}
          </div>
        </div>
      ))}
    </div>
  );
}
